import { useState, type ComponentProps } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";

import type { ProofPortal } from "constants/app_subdomains";
import { FeatureAnnouncement } from "common/core/announcements";
import { ACCOUNT_TYPES } from "common/account/constants";
import Button from "common/core/button";
import type TooltipOverlay from "common/core/tooltip/overlay";
import { useTagUserMutation } from "common/user/tag";
import { captureException } from "util/exception";
import Checkbox from "common/form/inputs/checkbox";
import { isIndependentNotary } from "common/notary/capacity";
import { useProofRebrand } from "util/feature_detection";

import type { ProofFrame_viewer_user as User } from "../index.query.graphql";
import {
  COMMON_MESSAGES,
  ORGANIZATION_APP_TAG,
  SIGNER_APP_TAG,
  ORGANIZATION_STEPS,
  ORGANIZATION_SIGNER_STEPS,
} from "./constants";
import {
  NOTARIZE_ORGANIZATION_STEPS,
  NOTARIZE_ORGANIZATION_SIGNER_STEPS,
} from "./notarize_constants";
import Styles from "./index.module.scss";

type Props = {
  portal: ProofPortal;
  sideNavCollapsed: boolean;
  user: User;
};

type ToolTipProps = ComponentProps<typeof TooltipOverlay>;

type UserApp = "title" | "lender" | "business" | "notary";

type OrganizationUserSteps = typeof ORGANIZATION_STEPS | typeof ORGANIZATION_SIGNER_STEPS;

type GetSteps = {
  isOrgUser: boolean;
  portal: ProofPortal;
};

type Step = {
  isNotary: boolean;
  portal: UserApp;
  step: number;
  steps: OrganizationUserSteps;
};

function useGetSteps({ isOrgUser, portal }: GetSteps) {
  const proofBrand = useProofRebrand();

  if (proofBrand) {
    if (portal !== "app") {
      return ORGANIZATION_STEPS;
    } else if (isOrgUser) {
      return ORGANIZATION_SIGNER_STEPS;
    }
  }

  if (portal !== "app") {
    return NOTARIZE_ORGANIZATION_STEPS;
  } else if (isOrgUser) {
    return NOTARIZE_ORGANIZATION_SIGNER_STEPS;
  }
}

function getStep({ isNotary, portal, step, steps }: Step) {
  const { heading, description, arrowPlacement, buttonLabel, buttonText, stepName } = steps[step];
  const showNotaryWidget = isNotary && stepName === "widget";

  return {
    heading,
    description: description[showNotaryWidget ? "notary" : portal],
    arrowPlacement,
    buttonLabel,
    buttonText,
    stepName,
  };
}

function getUserPortal(accountTypes: string[]) {
  if (accountTypes.includes(ACCOUNT_TYPES.TITLE_AGENCY.toUpperCase())) {
    return "title";
  } else if (accountTypes.includes(ACCOUNT_TYPES.LENDER.toUpperCase())) {
    return "lender";
  } else if (accountTypes.includes(ACCOUNT_TYPES.BUSINESS.toUpperCase())) {
    return "business";
  } else if (accountTypes.includes(ACCOUNT_TYPES.NOTARY.toUpperCase())) {
    return "notary";
  }
  return "business";
}

function steppedHeading(heading: string, step: number, totalSteps: number) {
  return `${heading} (${step + 1}/${totalSteps})`;
}

function IaWalkthrough({ portal, sideNavCollapsed, user }: Props) {
  const intl = useIntl();
  const tagUserMutateFn = useTagUserMutation();

  const appUserTag = portal === "app" ? SIGNER_APP_TAG : ORGANIZATION_APP_TAG;
  const hasDismissed = user.tags?.filter((tag) => tag?.tag === appUserTag) || [];
  const [showWalkthrough, setShowWalkthrough] = useState(!hasDismissed.length);
  const [dontShowAgain, setDontShowAgain] = useState(true);
  const [step, setStep] = useState(0);

  const hasOrganization = user.organization;
  // Because we auto-create orgs for signers that click into their settings, simply checking whether they
  // have an org is not enough to determine if someone is *just* a signer. We've agreed checking whether
  // the org has txns is a good enough gauge for this.
  const isOrgUser = hasOrganization
    ? portal !== "app" || user.organization.organizationTransactions.totalCount > 0
    : false;
  const isNotary = Boolean(user.notaryProfile);
  // We need the account type of the user to determine which description to show in the signer app
  const userPortal = isIndependentNotary(user.notaryProfile)
    ? "notary"
    : (getUserPortal(user.accountTypes as string[]) as UserApp);
  const steps = useGetSteps({ isOrgUser, portal });
  if (!steps) {
    return null;
  }

  const lastStep = step === steps.length - 1;

  const { heading, description, arrowPlacement, buttonLabel, buttonText, stepName } = getStep({
    isNotary,
    portal: userPortal,
    step,
    steps,
  });

  const formattedHeading =
    steps.length > 1
      ? steppedHeading(intl.formatMessage(heading), step, steps.length)
      : intl.formatMessage(heading);

  function handleDontShowAgain() {
    try {
      tagUserMutateFn({
        variables: {
          input: {
            userId: user.id,
            tag: appUserTag,
          },
          userTagList: [appUserTag],
        },
      });
    } catch (err) {
      captureException(new Error(`Error tagging IA walkthrough banner for user ${user.id}`));
    }
  }

  function handleNextClick() {
    const currentStep = step;

    if (lastStep) {
      dontShowAgain && handleDontShowAgain();
      setShowWalkthrough(false);
    } else {
      setStep(currentStep + 1);
    }
  }

  return showWalkthrough ? (
    <span
      className={classNames(
        Styles.walkthrough,
        Styles[stepName],
        sideNavCollapsed && Styles.collapsed,
      )}
    >
      <FeatureAnnouncement
        heading={formattedHeading}
        description={intl.formatMessage(description)}
        arrowPlacement={arrowPlacement as ToolTipProps["placement"]}
        customButton={
          <>
            {lastStep && (
              <div className={Styles.dontShowAgain}>
                <Checkbox
                  aria-label="dont-show-again-checkbox"
                  className={Styles.checkbox}
                  checked={dontShowAgain}
                  onChange={() => setDontShowAgain(!dontShowAgain)}
                />
                {intl.formatMessage(COMMON_MESSAGES.dontShowAgain)}
              </div>
            )}
            <Button
              buttonColor="action"
              variant="primary"
              aria-label={intl.formatMessage(buttonLabel)}
              automationId={`walkthrough-button-${buttonText}`}
              className={Styles.button}
              fullwidth
              onClick={handleNextClick}
            >
              {intl.formatMessage(buttonText)}
            </Button>
          </>
        }
      />
    </span>
  ) : null;
}

export default IaWalkthrough;
